import { Box, Typography, styled } from '@mui/material';
import React from 'react';

import { ChartFrame } from 'components/charts/chart-frame';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';

type Props = {
  label: React.ReactNode;
  value: React.ReactNode; // pre-formatted by the caller
  marker?: React.ReactNode; // drawn before the label, mark it aria-hidden when decorative
  children?: React.ReactNode;
};

const Root = styled(ChartFrame)(({ theme }) => ({
  gap: theme.spacing(3),
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
}));

const Label = styled(Typography, { shouldForwardProp: (prop) => prop !== 'chartPalette' })<{ chartPalette: ChartPalette }>(
  ({ chartPalette }) => ({
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: chartPalette.text.secondary,
  })
);

const Value = styled(Typography, { shouldForwardProp: (prop) => prop !== 'chartPalette' })<{ chartPalette: ChartPalette }>(
  ({ chartPalette }) => ({
    minWidth: 0,
    overflowWrap: 'anywhere',
    fontSize: 28,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    color: chartPalette.text.primary,
  })
);

// Children render as direct flex children below the value, so a caller can pin a footer with marginTop: 'auto'.
const KpiCard: React.FC<Props> = ({ label, value, marker, children }) => {
  const palette = useChartPalette();
  const animationDuration = useChartAnimation() ? 0 : 160;
  const labelNode = <Label chartPalette={palette}>{label}</Label>;

  return (
    <Root chartPalette={palette} animationDuration={animationDuration}>
      {marker ? (
        <Box display="flex" alignItems="center" gap={2}>
          {marker}
          {labelNode}
        </Box>
      ) : (
        labelNode
      )}
      <Value chartPalette={palette}>{value}</Value>
      {children}
    </Root>
  );
};

export default KpiCard;
